import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, QrCode, Redo2, RotateCcw, Save, Trash2, Undo2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { DiningTable } from '@/types'
import { useTheme } from '@/contexts/ThemeContext'
import { FloorCombobox } from '@/components/tables/FloorCombobox'
import { TableSeatRing } from '@/components/tables/TableSeatRing'
import { CHAIR_PAD, chairPositions, type TableShape } from '@/lib/tableChairs'
import {
  MAP_H_MAX,
  MAP_H_MIN,
  MAP_W_MAX,
  MAP_W_MIN,
  normalizeRotationDegrees,
  snapMapToGrid,
  suggestMapDimensions,
} from '@/lib/tableMapSizing'
import { toastHelpers } from '@/lib/toast-helpers'

const DUPLICATE_TABLE_NAME_MSG =
  'Table names must be unique. Another table already uses this name — choose a different name.'

type LayoutTable = DiningTable & {
  map_x: number
  map_y: number
  map_w: number
  map_h: number
  map_rotation: number
  shape: 'rectangle' | 'square' | 'round'
}

function seedLayoutRow(t: DiningTable, index: number): LayoutTable {
  const col = index % 6
  const row = Math.floor(index / 6)
  const shape = (t.shape as LayoutTable['shape'] | undefined) ?? 'rectangle'
  const suggested = suggestMapDimensions(t.seating_capacity ?? 4, shape)
  const hasW = t.map_w != null && Number.isFinite(Number(t.map_w)) && Number(t.map_w) > 0
  const hasH = t.map_h != null && Number.isFinite(Number(t.map_h)) && Number(t.map_h) > 0
  return {
    ...t,
    map_x: t.map_x ?? 28 + col * 145,
    map_y: t.map_y ?? 24 + row * 108,
    map_w: hasW ? Number(t.map_w) : suggested.map_w,
    map_h: hasH ? Number(t.map_h) : suggested.map_h,
    map_rotation: t.map_rotation ?? 0,
    shape,
  }
}

type Props = {
  tables: DiningTable[]
  selectedFloor: string
  onFloorChange: (floor: string) => void
  floors: string[]
  /**
   * Persist the given layout. May return a map of table id → fresh updated_at
   * etag so the builder can refresh its rows in place after a save, preventing
   * a second consecutive save from 409-ing against its own just-written rows.
   */
  onSave: (tables: LayoutTable[]) => Promise<Record<string, string> | void>
  onCreateFloor: (name: string) => Promise<void>
  onRenameFloor: (from: string, to: string) => Promise<void>
  onDeleteFloor: (name: string, moveTo: string) => Promise<void>
  onUpsertTable: (payload: {
    id?: string
    table_number: string
    seating_capacity: number
    location: string
    zone?: string | null
    is_occupied?: boolean
    map_x?: number
    map_y?: number
    map_w?: number
    map_h?: number
    map_rotation?: number
    shape?: string
    /** updated_at etag for optimistic locking; only used on edits */
    ifMatch?: string | null
  }) => Promise<void>
  onDeleteTable: (id: string) => Promise<void>
  /**
   * Open the dine-in QR sheet for a saved table. Optional so the builder still
   * renders anywhere the host has no QR dialog; the button is simply hidden.
   *
   * Handled by the parent rather than here because the table list already owns
   * one QR dialog, and two instances of the same sheet drift.
   */
  onShowQr?: (table: DiningTable) => void
  /**
   * Retained for backward compatibility only. The floor surface now sizes
   * itself to the available column width (see the ResizeObserver below), so the
   * builder no longer reads a per-floor logical canvas size or exposes a manual
   * resize control. The parent may still pass these; they are ignored here.
   */
  floorLayouts?: Record<string, { width: number; height: number }>
  onResizeFloor?: (name: string, dims: { width: number; height: number }) => Promise<void>
  /**
   * Notified whenever the builder has unsaved edits. The parent uses this to
   * suppress background table refetches while editing, so a live SSE event
   * can't refetch and reseed the floor mid-drag.
   */
  onDirtyChange?: (dirty: boolean) => void
}

// The floor surface fills the available column width (measured live) and grows
// downward — there is no zoom and no horizontal scroll. A table can never be
// dropped past the right edge (drag is clamped to the surface width), so the
// surface is never wider than the viewport. MIN_SURFACE_W keeps the drawable
// area usable on very narrow columns; the runway constants keep a screenful of
// empty space below the lowest table so operators can always drag further down.
const MIN_SURFACE_W = 320
const BASE_SURFACE_H = 720
const SURFACE_BOTTOM_RUNWAY = 400

// Five full-width table-footprint presets (S → XXL), each tuned to a typical
// party size at that scale. Widths/heights stay inside MAP_W/H bounds and snap
// to the 8px grid. The subtitle is shown under the label so operators can see
// roughly who each size seats without opening a legend.
const SIZE_PRESETS = [
  { label: 'S', hint: '1–2', map_w: 96, map_h: 64 },
  { label: 'M', hint: '3–4', map_w: 128, map_h: 80 },
  { label: 'L', hint: '5–6', map_w: 168, map_h: 96 },
  { label: 'XL', hint: '7–8', map_w: 224, map_h: 120 },
  { label: 'XXL', hint: '10+', map_w: 288, map_h: 160 },
] as const

// Touch drag ergonomics. On a finger, an instant drag is indistinguishable
// from a scroll/pan of the floor, so we gate touch dragging behind a
// press-and-hold: hold a table for LONG_PRESS_MS to "pick it up", then slide
// to move. Any finger travel past MOVE_CANCEL_PX before the hold completes
// cancels the pickup so a sloppy tap stays a plain selection. Mouse/pen are
// unaffected — they still drag instantly.
const LONG_PRESS_MS = 500
const MOVE_CANCEL_PX = 10

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n))
}

export function TableLayoutBuilder({
  tables,
  selectedFloor,
  onFloorChange,
  floors,
  onSave,
  onCreateFloor,
  onRenameFloor,
  onDeleteFloor,
  onUpsertTable,
  onDeleteTable,
  onShowQr,
  onDirtyChange,
}: Props) {
  const { theme } = useTheme()
  const isDarkTheme = theme === 'dark'
  const [gridSize] = useState(8)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingTable, setSavingTable] = useState(false)
  const [isCreatingTable, setIsCreatingTable] = useState(false)
  const [addFloorOpen, setAddFloorOpen] = useState(false)
  const [newFloorName, setNewFloorName] = useState('')
  const [creatingFloor, setCreatingFloor] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  // Set while a table is actively being dragged under a finger — drives the
  // "picked up" lift styling (scale/shadow/ring) so touch users can see the
  // long-press armed before they start moving. Null for mouse drags, which
  // don't need the extra affordance.
  const [draggingId, setDraggingId] = useState<string | null>(null)
  // Multi-select. activeId is always either null or the most-recently
  // clicked id (used by the right-side editor panel for single-table
  // properties). selectedIds includes activeId plus any Shift/Cmd-clicked
  // companions so arrow nudge / Delete / Cmd+D operate on all of them.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [layout, setLayout] = useState<LayoutTable[]>([])
  // Undo/redo history. Stored in a ref so snapshots don't trigger
  // re-renders themselves; historyVersion is bumped to refresh button
  // disabled states. Capped at HISTORY_CAP entries to bound memory.
  const HISTORY_CAP = 50
  const historyRef = useRef<{ past: LayoutTable[][]; future: LayoutTable[][] }>({
    past: [],
    future: [],
  })
  const [historyVersion, setHistoryVersion] = useState(0)
  const [tableNumberError, setTableNumberError] = useState<string | null>(null)
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const canvasShellRef = useRef<HTMLDivElement | null>(null)

  // The floor surface fills the width of its column (measured live via a
  // ResizeObserver) and grows tall enough to hold every table plus a runway
  // of empty space below the lowest one. There is no zoom and no horizontal
  // scroll: width tracks the viewport, height grows with content, and the
  // shell scrolls vertically. This keeps positions predictable while letting
  // operators keep dragging further down forever.
  const [surfaceW, setSurfaceW] = useState(960)

  useEffect(() => {
    const el = canvasShellRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width)
        if (w > 0) setSurfaceW(Math.max(MIN_SURFACE_W, w))
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const canvasSize = useMemo(() => {
    const bottom = layout.reduce(
      (max, t) => Math.max(max, (t.map_y ?? 0) + (t.map_h ?? 0)),
      0,
    )
    return {
      width: surfaceW,
      height: Math.max(BASE_SURFACE_H, bottom + SURFACE_BOTTOM_RUNWAY),
    }
  }, [surfaceW, layout])

  const floorTables = useMemo(
    () => tables.filter((t) => (t.location || 'General') === selectedFloor),
    [tables, selectedFloor]
  )

  // Tracks which floor the local `layout` was last seeded from, so we can tell
  // an explicit floor switch (always reseed) apart from a background data
  // refetch on the same floor (only reseed when there are no unsaved edits).
  const seededFloorRef = useRef<string | null>(null)
  // Mirror of `dirty` for the reseed guard. We read it as a ref (not a dep) so
  // that flipping `dirty` never itself re-runs the reseed effect — otherwise
  // clearing dirty after a save would reseed from still-stale query data and
  // wipe the fresh etags we just applied in place (reintroducing the 409).
  const dirtyRef = useRef(false)
  useEffect(() => {
    dirtyRef.current = dirty
    // Keep the parent informed so it can pause background table refetches while
    // the operator is mid-edit.
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  useEffect(() => {
    // Seed the editable layout from server data — WITHOUT clobbering unsaved
    // drags. During live service the ['tables-summary'] query refetches
    // constantly (SSE: order_created / payment / table_status_changed …), which
    // used to re-run this effect mid-edit and silently reset both the operator's
    // in-progress positions AND the dirty flag. That was the root cause of
    // "I moved three tables but Save only kept the first": every event between
    // moves snapped the layout back to server state before Save ever read it.
    //
    // Rule: always reseed when the floor changes (explicit navigation) or on the
    // first load for a floor; on the same floor, only reseed while there are no
    // unsaved edits. This effect deliberately does NOT depend on `dirty` — it
    // fires only when the floor changes or fresh server data arrives.
    const floorChanged = seededFloorRef.current !== selectedFloor
    if (!floorChanged && dirtyRef.current) return

    const seeded = floorTables.map((t, index) => seedLayoutRow(t, index))
    setLayout(seeded)
    setDirty(false)
    setActiveId(null)
    setSelectedIds(new Set())
    // Drop undo/redo history whenever we reseed — the operator's mental model
    // resets, and the past stack would point to stale rows.
    historyRef.current = { past: [], future: [] }
    setHistoryVersion((v) => v + 1)
    seededFloorRef.current = selectedFloor
  }, [floorTables, selectedFloor])

  // Capture the current `layout` to the undo stack and clear redo. Call
  // BEFORE the mutation so the snapshot reflects pre-edit state.
  const snapshotForUndo = () => {
    const snap = layout.map((t) => ({ ...t }))
    const past = [...historyRef.current.past, snap]
    if (past.length > HISTORY_CAP) past.shift()
    historyRef.current = { past, future: [] }
    setHistoryVersion((v) => v + 1)
  }

  const undo = () => {
    const { past, future } = historyRef.current
    if (past.length === 0) return
    const prev = past[past.length - 1]
    historyRef.current = {
      past: past.slice(0, -1),
      future: [...future, layout.map((t) => ({ ...t }))],
    }
    setLayout(prev)
    setDirty(true)
    setHistoryVersion((v) => v + 1)
  }

  const redo = () => {
    const { past, future } = historyRef.current
    if (future.length === 0) return
    const next = future[future.length - 1]
    historyRef.current = {
      past: [...past, layout.map((t) => ({ ...t }))],
      future: future.slice(0, -1),
    }
    setLayout(next)
    setDirty(true)
    setHistoryVersion((v) => v + 1)
  }

  const canUndo = historyRef.current.past.length > 0
  const canRedo = historyRef.current.future.length > 0
  // Reference historyVersion so React re-renders the undo/redo buttons
  // when canUndo/canRedo flip.
  void historyVersion

  const onPointerDown = (event: React.PointerEvent, tableId: string) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const table = layout.find((t) => t.id === tableId)
    if (!table) return

    const rect = canvas.getBoundingClientRect()
    const offsetX = event.clientX - rect.left - table.map_x
    const offsetY = event.clientY - rect.top - table.map_y

    // Snapshot before a potential drag so the user can Cmd+Z back to
    // where they started, even if they only moved a single pixel.
    let snapshotted = false
    const ensureSnapshot = () => {
      if (snapshotted) return
      snapshotForUndo()
      snapshotted = true
    }

    // Selection logic. Shift / Cmd / Ctrl click toggles the table in the
    // multi-selection set without unselecting the rest. A plain click
    // replaces the selection with just this table.
    const additive = event.shiftKey || event.metaKey || event.ctrlKey
    setSelectedIds((prev) => {
      if (additive) {
        const next = new Set(prev)
        if (next.has(tableId)) {
          next.delete(tableId)
        } else {
          next.add(tableId)
        }
        return next
      }
      return new Set([tableId])
    })
    setActiveId(tableId)
    const captureEl = event.currentTarget as HTMLDivElement
    captureEl.setPointerCapture(event.pointerId)

    // The actual reposition. Only ever called once the drag is "armed"
    // (immediately for mouse/pen, after the long-press for touch).
    const onMove = (moveEvent: PointerEvent) => {
      ensureSnapshot()
      const xRaw = moveEvent.clientX - rect.left - offsetX
      const yRaw = moveEvent.clientY - rect.top - offsetY
      setLayout((prev) =>
        prev.map((item) => {
          if (item.id !== tableId) return item
          const xSnapped = Math.round(xRaw / gridSize) * gridSize
          const ySnapped = Math.round(yRaw / gridSize) * gridSize
          // Reserve room for the chair ring on all four sides so chairs
          // never get clipped at the canvas edges.
          return {
            ...item,
            map_x: clamp(xSnapped, CHAIR_PAD, canvasSize.width - item.map_w - CHAIR_PAD),
            map_y: clamp(ySnapped, CHAIR_PAD, canvasSize.height - item.map_h - CHAIR_PAD),
          }
        })
      )
      setDirty(true)
    }

    // Touch requires a press-and-hold before dragging; mouse/pen drag
    // immediately. `longPressTimer` is null once the drag is armed.
    const isTouch = event.pointerType === 'touch'
    let longPressTimer: ReturnType<typeof setTimeout> | null = null
    const startX = event.clientX
    const startY = event.clientY

    // Pre-arm listener (touch only): a sloppy tap that drifts past the
    // threshold before the hold completes cancels the pickup and falls
    // through as a plain selection, rather than snatching the table.
    const onPreArmMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - startX
      const dy = moveEvent.clientY - startY
      if (dx * dx + dy * dy > MOVE_CANCEL_PX * MOVE_CANCEL_PX) {
        cleanup()
      }
    }

    const arm = () => {
      longPressTimer = null
      window.removeEventListener('pointermove', onPreArmMove)
      setDraggingId(tableId)
      // Haptic confirmation on devices that support it (Android/Chrome);
      // iPad Safari silently ignores it, so this degrades gracefully.
      navigator.vibrate?.(30)
      window.addEventListener('pointermove', onMove)
    }

    const cleanup = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer)
        longPressTimer = null
      }
      window.removeEventListener('pointermove', onPreArmMove)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      try {
        captureEl.releasePointerCapture(event.pointerId)
      } catch {
        /* pointer may already be released */
      }
      setDraggingId(null)
    }

    const onUp = () => {
      cleanup()
    }

    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)

    if (isTouch) {
      window.addEventListener('pointermove', onPreArmMove)
      longPressTimer = setTimeout(arm, LONG_PRESS_MS)
    } else {
      arm()
    }
  }

  // Nudge every selected table by (dx, dy), clamped to canvas + chair pad.
  const nudgeSelection = (dx: number, dy: number) => {
    if (selectedIds.size === 0) return
    snapshotForUndo()
    setLayout((prev) =>
      prev.map((item) => {
        if (!selectedIds.has(item.id)) return item
        return {
          ...item,
          map_x: clamp(
            item.map_x + dx,
            CHAIR_PAD,
            canvasSize.width - item.map_w - CHAIR_PAD,
          ),
          map_y: clamp(
            item.map_y + dy,
            CHAIR_PAD,
            canvasSize.height - item.map_h - CHAIR_PAD,
          ),
        }
      }),
    )
    setDirty(true)
  }

  // Duplicate every selected table. The duplicates are created server-side
  // via onUpsertTable; we offset their position so they don't sit on top
  // of the originals. Names get a " copy" suffix to avoid the unique-name
  // collision; the operator can rename afterwards.
  const duplicateSelection = async () => {
    if (selectedIds.size === 0) return
    snapshotForUndo()
    const targets = layout.filter((t) => selectedIds.has(t.id))
    for (const t of targets) {
      const baseName = t.table_number || 'Table'
      let candidate = `${baseName} copy`
      let i = 2
      while (layout.some((x) => x.table_number === candidate)) {
        candidate = `${baseName} copy ${i}`
        i += 1
      }
      try {
        await onUpsertTable({
          table_number: candidate,
          seating_capacity: t.seating_capacity,
          location: t.location || selectedFloor,
          zone: null,
          map_x: clamp(t.map_x + 24, CHAIR_PAD, canvasSize.width - t.map_w - CHAIR_PAD),
          map_y: clamp(t.map_y + 24, CHAIR_PAD, canvasSize.height - t.map_h - CHAIR_PAD),
          map_w: t.map_w,
          map_h: t.map_h,
          map_rotation: t.map_rotation,
          shape: t.shape,
        })
      } catch {
        /* toast emitted by parent; stop the batch on first error */
        break
      }
    }
  }

  // Delete every selected table, skipping any with active orders. Returns
  // a summary the caller can use for a toast.
  //
  // A delete is a server write, not a layout edit, so the deleted rows are
  // removed from the local floor directly. Selecting a table with the mouse
  // runs the drag path and marks the floor dirty, and a dirty floor ignores
  // the refetch that would otherwise remove them — so without this the table
  // stayed drawn after a successful delete and looked like nothing happened.
  // They are stripped from undo/redo too: undo cannot bring back a table that
  // no longer exists on the server, and Save would then send a dead id.
  const dropDeletedTables = (ids: string[]) => {
    if (ids.length === 0) return
    const gone = new Set(ids)
    const keep = (rows: LayoutTable[]) => rows.filter((t) => !gone.has(t.id))
    setLayout((prev) => keep(prev))
    historyRef.current = {
      past: historyRef.current.past.map(keep),
      future: historyRef.current.future.map(keep),
    }
    setHistoryVersion((v) => v + 1)
  }

  const deleteSelection = async () => {
    if (selectedIds.size === 0) return
    const targets = layout.filter((t) => selectedIds.has(t.id))
    const skipped: string[] = []
    const deleted: string[] = []
    for (const t of targets) {
      const occupied = t.has_active_order ?? t.is_occupied
      if (occupied) {
        skipped.push(t.table_number)
        continue
      }
      try {
        await onDeleteTable(t.id)
        deleted.push(t.id)
      } catch {
        /* toast from parent; keep going */
      }
    }
    dropDeletedTables(deleted)
    setSelectedIds(new Set())
    setActiveId(null)
    if (skipped.length > 0) {
      toastHelpers.warning(
        'Some tables not deleted',
        `Occupied tables were skipped: ${skipped.join(', ')}.`,
      )
    }
  }

  // Global keyboard shortcuts for the builder. We attach to window because
  // the canvas itself isn't focusable; the listener bails if the user is
  // typing into an input/textarea so we don't steal Cmd+D while they're
  // editing a table name.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const inText =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      if (inText) return

      if (e.key === 'Escape') {
        setSelectedIds(new Set())
        setActiveId(null)
        return
      }
      // Undo / redo. Bound at the top-level (independent of selection) so
      // operators can keep undoing past their current selection.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault()
        if (e.shiftKey) redo()
        else undo()
        return
      }
      // Y is the conventional Windows redo shortcut.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault()
        redo()
        return
      }
      if (selectedIds.size === 0) return

      // Arrow keys nudge; Shift = larger step.
      const step = e.shiftKey ? gridSize : 1
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        nudgeSelection(-step, 0)
        return
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        nudgeSelection(step, 0)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        nudgeSelection(0, -step)
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        nudgeSelection(0, step)
        return
      }
      // Cmd/Ctrl + D duplicates.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault()
        void duplicateSelection()
        return
      }
      // Delete / Backspace removes (Backspace also covers macOS where there
      // is no dedicated Delete key on most laptops).
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        void deleteSelection()
        return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, canvasSize.width, canvasSize.height, gridSize, layout])

  const activeTable = layout.find((t) => t.id === activeId) ?? null
  const newTableDims = suggestMapDimensions(4, 'rectangle')
  const selectedTable = isCreatingTable
    ? ({
        id: '',
        table_number: '',
        seating_capacity: 4,
        location: selectedFloor,
        is_occupied: false,
        has_active_order: false,
        created_at: '',
        updated_at: '',
        map_x: 48,
        map_y: 48,
        map_w: newTableDims.map_w,
        map_h: newTableDims.map_h,
        map_rotation: 0,
        shape: 'rectangle',
      } as LayoutTable)
    : activeTable
  const [draft, setDraft] = useState<LayoutTable | null>(null)

  useEffect(() => {
    setDraft(selectedTable ? { ...selectedTable } : null)
  }, [activeId, isCreatingTable, selectedFloor])

  const applyActivePatch = (patch: Partial<LayoutTable>) => {
    if (!activeId) return
    snapshotForUndo()
    setLayout((prev) => prev.map((t) => (t.id === activeId ? { ...t, ...patch } : t)))
    setDirty(true)
  }

  // Right-panel edits flow through here. Snapshot the FIRST keystroke for
  // a given active table; subsequent keystrokes within 800 ms or on the
  // same active table coalesce into the same undo step so Cmd+Z doesn't
  // require 12 presses to undo "Table 4" -> "Table 40".
  const lastPatchSnapshotRef = useRef<{ tableId: string | null; at: number }>({
    tableId: null,
    at: 0,
  })
  const patchLayoutForActive = (patch: Partial<LayoutTable>) => {
    if (!activeId || isCreatingTable) return
    const now = Date.now()
    const ref = lastPatchSnapshotRef.current
    if (ref.tableId !== activeId || now - ref.at > 800) {
      snapshotForUndo()
      lastPatchSnapshotRef.current = { tableId: activeId, at: now }
    } else {
      lastPatchSnapshotRef.current = { tableId: activeId, at: now }
    }
    setLayout((prev) => prev.map((t) => (t.id === activeId ? { ...t, ...patch } : t)))
    setDirty(true)
  }

  const handleSeatsChange = (raw: string) => {
    if (!draft) return
    const seating_capacity = Math.max(1, Math.min(20, Math.round(Number(raw)) || 1))
    const next = { ...draft, seating_capacity }
    setDraft(next)
    patchLayoutForActive(next)
  }

  const handleShapeChange = (shape: LayoutTable['shape']) => {
    if (!draft) return
    const next = { ...draft, shape }
    setDraft(next)
    patchLayoutForActive(next)
  }

  const handleMapWChange = (raw: string) => {
    if (!draft) return
    const map_w = snapMapToGrid(clamp(Number(raw) || MAP_W_MIN, MAP_W_MIN, MAP_W_MAX))
    setDraft({ ...draft, map_w })
    patchLayoutForActive({ map_w })
  }

  const handleMapHChange = (raw: string) => {
    if (!draft) return
    const map_h = snapMapToGrid(clamp(Number(raw) || MAP_H_MIN, MAP_H_MIN, MAP_H_MAX))
    setDraft({ ...draft, map_h })
    patchLayoutForActive({ map_h })
  }

  // Full-width S→XXL sizing presets. Each maps to a footprint tuned for a
  // typical party size at that scale; the operator can still fine-tune via the
  // Width/Height inputs afterwards. Applies to both an existing selection and
  // the in-progress "new table" draft.
  const applySizePreset = (preset: (typeof SIZE_PRESETS)[number]) => {
    if (!draft) return
    const map_w = snapMapToGrid(clamp(preset.map_w, MAP_W_MIN, MAP_W_MAX))
    const map_h = snapMapToGrid(clamp(preset.map_h, MAP_H_MIN, MAP_H_MAX))
    setDraft({ ...draft, map_w, map_h })
    patchLayoutForActive({ map_w, map_h })
  }

  // Which preset (if any) the current draft footprint matches, so the active
  // size button can be highlighted.
  const activeSizeLabel = useMemo(() => {
    if (!draft) return null
    const match = SIZE_PRESETS.find(
      (p) =>
        snapMapToGrid(clamp(p.map_w, MAP_W_MIN, MAP_W_MAX)) === Math.round(draft.map_w) &&
        snapMapToGrid(clamp(p.map_h, MAP_H_MIN, MAP_H_MAX)) === Math.round(draft.map_h),
    )
    return match?.label ?? null
  }, [draft])

  const handleRotationChange = (raw: string) => {
    if (!draft) return
    const n = Number(raw)
    const map_rotation = Number.isFinite(n) ? normalizeRotationDegrees(Math.round(n)) : 0
    setDraft({ ...draft, map_rotation })
    patchLayoutForActive({ map_rotation })
  }

  const handleRotationBlur = () => {
    if (!draft) return
    const map_rotation = normalizeRotationDegrees(draft.map_rotation)
    if (map_rotation !== draft.map_rotation) {
      setDraft({ ...draft, map_rotation })
      patchLayoutForActive({ map_rotation })
    }
  }

  const setRotationPreset = (deg: number) => {
    if (!draft) return
    const map_rotation = normalizeRotationDegrees(deg)
    setDraft({ ...draft, map_rotation })
    patchLayoutForActive({ map_rotation })
  }

  const resetFloor = () => {
    const seeded = floorTables.map((t, index) => seedLayoutRow(t, index))
    setLayout(seeded)
    setDirty(false)
    setActiveId(null)
    setSelectedIds(new Set())
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const freshEtags = await onSave(layout)
      // Refresh optimistic-lock etags in place (positions untouched) so an
      // immediate second save doesn't 409 against the rows we just wrote.
      if (freshEtags) {
        setLayout((prev) =>
          prev.map((t) =>
            freshEtags[t.id] ? { ...t, updated_at: freshEtags[t.id] } : t,
          ),
        )
      }
      setDirty(false)
      // After a successful save, prior unsaved edits are now persistent
      // history we can't roll back from. Clear the undo stack so the
      // controls accurately reflect what's reversible in-session.
      historyRef.current = { past: [], future: [] }
      setHistoryVersion((v) => v + 1)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveTable = async (): Promise<boolean> => {
    if (!draft) return false
    const trimmed = draft.table_number.trim()
    if (!trimmed) return false

    const duplicate = tables.some((t) => {
      if (!isCreatingTable && t.id === draft.id) return false
      return t.table_number.trim() === trimmed
    })
    if (duplicate) {
      setTableNumberError(DUPLICATE_TABLE_NAME_MSG)
      toastHelpers.validationError(DUPLICATE_TABLE_NAME_MSG)
      return false
    }
    setTableNumberError(null)

    setSavingTable(true)
    try {
      await onUpsertTable({
        id: isCreatingTable ? undefined : draft.id,
        table_number: trimmed,
        seating_capacity: draft.seating_capacity,
        location: draft.location || selectedFloor,
        zone: null,
        map_x: draft.map_x,
        map_y: draft.map_y,
        map_w: draft.map_w,
        map_h: draft.map_h,
        map_rotation: draft.map_rotation,
        shape: draft.shape,
        ifMatch: isCreatingTable ? null : draft.updated_at || null,
      })
      setIsCreatingTable(false)
      setActiveId(null)
      setSelectedIds(new Set())
      return true
    } finally {
      setSavingTable(false)
    }
  }

  const handleDeleteSelectedTable = async () => {
    if (!draft || isCreatingTable || !draft.id) return
    await onDeleteTable(draft.id)
    dropDeletedTables([draft.id])
    setActiveId(null)
    setSelectedIds(new Set())
  }

  const handleAddFloor = async () => {
    const name = newFloorName.trim()
    if (!name) return
    setCreatingFloor(true)
    try {
      await onCreateFloor(name)
      onFloorChange(name)
      setNewFloorName('')
      setAddFloorOpen(false)
    } finally {
      setCreatingFloor(false)
    }
  }

  const statusCounts = useMemo(() => {
    const available = layout.filter((t) => !(t.has_active_order ?? t.is_occupied)).length
    const occupied = layout.filter((t) => t.has_active_order ?? t.is_occupied).length
    return { available, occupied }
  }, [layout])

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border bg-card px-3 py-2.5 shadow-sm xl:flex-row xl:items-center xl:justify-between">
        {/* Left: floor selector + add floor + live occupancy */}
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="min-w-[200px] max-w-xs flex-1">
            <FloorCombobox
              value={selectedFloor}
              onValueChange={onFloorChange}
              options={floors}
              onCreateFloor={onCreateFloor}
              onRenameFloor={onRenameFloor}
              onDeleteFloor={onDeleteFloor}
              allowCreate={false}
              manageable
              placeholder="Floor — search or select"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-9 gap-1.5"
            onClick={() => setAddFloorOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add Floor
          </Button>
          <div className="mx-0.5 hidden h-6 w-px bg-border sm:block" />
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusChip tone="available" label={`${statusCounts.available} available`} />
            <StatusChip tone="occupied" label={`${statusCounts.occupied} occupied`} />
            {selectedIds.size > 1 ? (
              <StatusChip tone="selected" label={`${selectedIds.size} selected`} />
            ) : null}
          </div>
        </div>

        {/* Right: history · reset · add table · save */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center rounded-lg border bg-background p-0.5 shadow-sm">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 px-2.5"
              onClick={undo}
              disabled={!canUndo}
              title="Undo (⌘Z)"
            >
              <Undo2 className="h-4 w-4" />
              Undo
            </Button>
            <span className="mx-0.5 h-5 w-px bg-border" />
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 px-2.5"
              onClick={redo}
              disabled={!canRedo}
              title="Redo (⇧⌘Z)"
            >
              <Redo2 className="h-4 w-4" />
              Redo
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-1.5"
            onClick={resetFloor}
            title="Discard unsaved changes on this floor"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-9 gap-1.5"
            onClick={() => { setIsCreatingTable(true); setActiveId(null) }}
          >
            <Plus className="h-4 w-4" />
            Add Table
          </Button>
          <Button
            size="sm"
            className="h-9 gap-1.5"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {dirty && !saving ? (
              <span className="h-1.5 w-1.5 rounded-full bg-amber-300" aria-hidden />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving…' : 'Save layout'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-3">
        <div
          ref={canvasShellRef}
          className="overflow-x-hidden overflow-y-auto rounded-xl border bg-[#f4efe6] dark:bg-[#1f1b17] min-h-[560px] max-h-[calc(100vh-15rem)]"
        >
          <div
            ref={canvasRef}
            onPointerDown={(e) => {
              // Click on empty floor space clears any multi-selection. We
              // only act if the user actually clicked the canvas surface
              // itself, not a descendant (tables stop propagation via their
              // own onPointerDown handlers).
              if (e.target === e.currentTarget) {
                setSelectedIds(new Set())
                setActiveId(null)
              }
            }}
            /*
             * The floor-plan canvas is a deliberate warm-parchment surface, not
             * a token surface — it reads as "the room". In dark mode it kept
             * its light parchment and became the single largest bright slab in
             * the app (F-FE-N55). It keeps its character in both themes: warm
             * paper in light, warm wood in dark. The grid lines invert with it,
             * since dark-on-dark grid ruling is invisible.
             */
            className="relative bg-[#f7f1e8] dark:bg-[#26211b]"
            style={{
              width: `${canvasSize.width}px`,
              height: `${canvasSize.height}px`,
              backgroundImage: isDarkTheme
                ? 'linear-gradient(to right, rgba(255,238,214,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,238,214,0.05) 1px, transparent 1px)'
                : 'linear-gradient(to right, rgba(74,53,33,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(74,53,33,0.06) 1px, transparent 1px)',
              backgroundSize: `${gridSize}px ${gridSize}px`,
            }}
          >
            {layout.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-muted-foreground">
                No tables on this floor yet. Use Add Table above or add tables from the table list.
              </div>
            )}
            {layout.map((table) => {
              const occupied = table.has_active_order ?? table.is_occupied
              const isSelected = selectedIds.has(table.id)
              const isFocused = activeId === table.id
              // Chair color tracks the table's role: muted for available,
              // accent for occupied, primary for the selection focus.
              const chairColorClass = isSelected
                ? 'text-primary'
                : occupied
                  ? 'text-emerald-600'
                  : 'text-muted-foreground/70'
              const chairs = chairPositions(
                table.seating_capacity ?? 0,
                (table.shape as TableShape) ?? 'rectangle',
                table.map_w,
                table.map_h,
              )
              // Keep every table inside the visible surface even if its saved
              // x/y predates the current (narrower) width — a table can never be
              // clipped off the right edge. Purely a render clamp; the saved
              // coordinate is untouched until the operator drags it.
              const renderX = clamp(
                table.map_x,
                CHAIR_PAD,
                Math.max(CHAIR_PAD, canvasSize.width - table.map_w - CHAIR_PAD),
              )
              const renderY = clamp(
                table.map_y,
                CHAIR_PAD,
                Math.max(CHAIR_PAD, canvasSize.height - table.map_h - CHAIR_PAD),
              )
              return (
                <div
                  key={table.id}
                  className={`absolute ${chairColorClass}`}
                  style={{
                    left: `${renderX}px`,
                    top: `${renderY}px`,
                    width: `${table.map_w}px`,
                    height: `${table.map_h}px`,
                    transform: `rotate(${table.map_rotation}deg)`,
                    transformOrigin: 'center center',
                  }}
                >
                  <TableSeatRing
                    positions={chairs}
                    tableWidth={table.map_w}
                    tableHeight={table.map_h}
                  />
                  <div
                    onPointerDown={(e) => onPointerDown(e, table.id)}
                    className={`relative h-full w-full select-none border cursor-move flex flex-col items-center justify-center text-sm transition-transform ${
                      draggingId === table.id
                        ? 'scale-105 shadow-lg ring-2 ring-primary z-10'
                        : isFocused
                          ? 'shadow-sm ring-2 ring-primary'
                          : isSelected
                            ? 'shadow-sm ring-2 ring-primary/40'
                            : 'shadow-sm'
                    } ${occupied ? 'bg-success-surface border-success-solid' : 'bg-card border-border'}`}
                    style={{
                      borderRadius: table.shape === 'round' ? '999px' : '10px',
                      // A finger that lands on a table should pick it up
                      // (via long-press), never scroll the floor — scrolling
                      // still works from empty canvas space.
                      touchAction: 'none',
                    }}
                  >
                    <div className="font-semibold leading-none text-foreground">
                      {table.table_number}
                    </div>
                    <div className="opacity-70 text-[11px] text-foreground">
                      {table.seating_capacity} seats
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <Card className="self-start lg:sticky lg:top-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {isCreatingTable ? 'New Table' : 'Selected Table'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!draft ? (
              <p className="text-sm text-muted-foreground">Select a table to edit its details and layout.</p>
            ) : (
              <>
                  <div>
                    <label className="text-xs text-muted-foreground">Table Number</label>
                    <Input
                      value={draft.table_number}
                      onChange={(e) => {
                        setTableNumberError(null)
                        setDraft({ ...draft, table_number: e.target.value })
                      }}
                      className="mt-1"
                      aria-invalid={tableNumberError ? true : undefined}
                    />
                    {tableNumberError ? (
                      <p className="text-xs text-destructive mt-1">{tableNumberError}</p>
                    ) : null}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Location / Floor</label>
                    <div className="mt-1">
                      <FloorCombobox
                        value={draft.location || selectedFloor}
                        onValueChange={(v) => setDraft({ ...draft, location: v })}
                        options={floors}
                        onCreateFloor={onCreateFloor}
                        onRenameFloor={onRenameFloor}
                        onDeleteFloor={onDeleteFloor}
                        placeholder="Search or create floor"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground">Seats</label>
                      <Input
                        type="number"
                        min={1}
                        max={20}
                        value={draft.seating_capacity}
                        onChange={(e) => handleSeatsChange(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Shape</label>
                      <select
                        className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm mt-1"
                        value={draft.shape}
                        onChange={(e) => handleShapeChange(e.target.value as LayoutTable['shape'])}
                      >
                        <option value="rectangle">Rectangle</option>
                        <option value="square">Square</option>
                        <option value="round">Round</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Size</label>
                    <div className="grid grid-cols-5 gap-1.5">
                      {SIZE_PRESETS.map((p) => {
                        const active = activeSizeLabel === p.label
                        return (
                          <button
                            key={p.label}
                            type="button"
                            onClick={() => applySizePreset(p)}
                            title={`${p.map_w} × ${p.map_h}px · seats ${p.hint}`}
                            className={cn(
                              'flex flex-col items-center justify-center rounded-md border px-1 py-2 transition-colors',
                              active
                                ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                                : 'border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
                            )}
                          >
                            <span className="text-xs font-semibold leading-none">{p.label}</span>
                            <span
                              className={cn(
                                'mt-1 text-[10px] leading-none',
                                active ? 'text-primary-foreground/80' : 'text-muted-foreground',
                              )}
                            >
                              {p.hint}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground">Width</label>
                      <input
                        type="number"
                        min={MAP_W_MIN}
                        max={MAP_W_MAX}
                        step={8}
                        className="w-full p-2 border border-input rounded-md bg-background text-sm mt-1"
                        value={Math.round(draft.map_w)}
                        onChange={(e) => handleMapWChange(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Height</label>
                      <input
                        type="number"
                        min={MAP_H_MIN}
                        max={MAP_H_MAX}
                        step={8}
                        className="w-full p-2 border border-input rounded-md bg-background text-sm mt-1"
                        value={Math.round(draft.map_h)}
                        onChange={(e) => handleMapHChange(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground">Rotation (degrees)</label>
                    <Input
                      type="number"
                      min={0}
                      max={359}
                      step={1}
                      value={draft.map_rotation}
                      onChange={(e) => handleRotationChange(e.target.value)}
                      onBlur={handleRotationBlur}
                      className="mt-1"
                    />
                    <div className="grid grid-cols-4 gap-1.5">
                      {([0, 90, 180, 270] as const).map((deg) => (
                        <Button
                          key={deg}
                          type="button"
                          variant={draft.map_rotation === deg ? 'default' : 'outline'}
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={() => setRotationPreset(deg)}
                        >
                          {deg}°
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Button
                      size="sm"
                      className="h-9"
                      onClick={async () => {
                        try {
                          const saved = await handleSaveTable()
                          if (
                            saved &&
                            !isCreatingTable &&
                            activeId
                          ) {
                            applyActivePatch({
                              table_number: draft.table_number,
                              seating_capacity: draft.seating_capacity,
                              location: draft.location,
                              shape: draft.shape,
                              map_w: draft.map_w,
                              map_h: draft.map_h,
                              map_rotation: draft.map_rotation,
                            })
                          }
                        } catch {
                          /* toast from parent */
                        }
                      }}
                      disabled={savingTable}
                    >
                      {savingTable ? 'Saving…' : 'Save Table'}
                    </Button>
                    {/*
                      Only for a table that exists server-side. A table being
                      created has no id yet, so it has no slug to encode and the
                      sheet would have nothing to show.
                    */}
                    {!isCreatingTable && onShowQr && selectedTable && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-9 gap-1.5"
                        onClick={() => onShowQr(selectedTable as DiningTable)}
                      >
                        <QrCode className="h-4 w-4" />
                        QR
                      </Button>
                    )}
                    {!isCreatingTable ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-9 gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => void handleDeleteSelectedTable()}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-9"
                        onClick={() => setIsCreatingTable(false)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={addFloorOpen} onOpenChange={setAddFloorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Floor</DialogTitle>
          </DialogHeader>
          <Input
            value={newFloorName}
            onChange={(e) => setNewFloorName(e.target.value)}
            placeholder="Enter floor name"
            onKeyDown={async (e) => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              await handleAddFloor()
            }}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAddFloorOpen(false)
                setNewFloorName('')
              }}
            >
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleAddFloor()} disabled={creatingFloor}>
              {creatingFloor ? 'Adding...' : 'Add Floor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function StatusChip({
  tone,
  label,
}: {
  tone: 'available' | 'occupied' | 'selected'
  label: string
}) {
  const toneClass = {
    available: 'border-success-border bg-success-surface text-success-fg',
    occupied: 'border-border bg-muted text-muted-foreground',
    selected: 'border-primary/30 bg-primary/10 text-primary',
  }[tone]
  const dotClass = {
    available: 'bg-emerald-500',
    occupied: 'bg-muted-foreground',
    selected: 'bg-primary',
  }[tone]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
        toneClass,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', dotClass)} />
      {label}
    </span>
  )
}

